import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { L10nTranslationService } from "angular-l10n";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { Coupon, CouponGen, CouponModel, Packages, SubscriptionEditModel, SubscriptionInfo } from "./subscription-models";
import { BoolResult } from "../module-thryve/thryve.service";
import { first } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  baseUri: string;

  constructor(private http: HttpClient,
    public translation: L10nTranslationService
  ) {
    if (window.location.href.includes('predemplatform')) {
      this.baseUri = `${environment.apiUrl}`;
    } else {
      this.baseUri = `${environment.apiUrlKog}`;
    }

  }

  /**
   * Előfizetés.
   */
  subscription(): Observable<SubscriptionEditModel> {
    return this.http.get<SubscriptionEditModel>(this.baseUri + 'Subscription/Subscription/');
  }

  async getSubscription() {
    let model: SubscriptionEditModel = {} as SubscriptionEditModel;
    return new Promise((resolve, reject) => {
      this.subscription().pipe(first()).subscribe({
        next: (v) => model = v,
        error: (e) => reject(e.message),
        complete: () => resolve(model)
      })
    });
  }

  isUserNameAvailable(userName: string): Observable<BoolResult> {
    return this.http.get<BoolResult>(this.baseUri + 'AccountFunc/IsUserNameAvailable?userName=' + encodeURIComponent(userName));
  }

  isCouponCodeAvailable(couponCode: string): Observable<BoolResult> {
    return this.http.get<BoolResult>(this.baseUri + 'Subscription/isCouponCodeAvailable?couponCode=' + encodeURIComponent(couponCode));
  }

  checkCouponCode(couponCode: string): Observable<BoolResult> {
    return this.http.get<BoolResult>(this.baseUri + 'Subscription/CheckCouponCode?couponCode=' + encodeURIComponent(couponCode));
  }

  postSubscription(data: SubscriptionEditModel): Observable<any> {
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' })
    };
    return this.http.post(this.baseUri + 'Subscription/SubscriptionPost', data, options);
  }

  addFreeGoldSubscription(value: string): Observable<HttpResponse<string>> {
    return this.http.post(this.baseUri + 'Subscription/AddFreeGoldSubscription', JSON.stringify(value), { headers: { 'Content-Type': 'application/json;charset=UTF-8' }, observe: 'response', responseType: 'text' });
  }

  recreateInvoice(subscriptionId: number): Observable<BoolResult> {
    return this.http.get<BoolResult>(this.baseUri + 'Subscription/ReCreateInvoice?subscriptionId=' + encodeURIComponent(subscriptionId));
  }

  useCoupon(data: CouponModel): Observable<HttpResponse<string>> {
    return this.http.post(this.baseUri + 'Subscription/UseCoupon', data, { headers: { 'Content-Type': 'application/json;charset=UTF-8' }, observe: 'response', responseType: 'text' });
  }
  generateCouponsGP(data: CouponGen): Observable<any> {
    return this.http.post(this.baseUri + 'Subscription/GenerateCouponGP', data, { headers: { 'Content-Type': 'application/json;charset=UTF-8' }, observe: 'response', responseType: 'blob' });
  }

  /**
   * Fizetés eredménye, amit a SimplePay visszadott.
   */
  getPaymentResult(r: string, s: string): Observable<SubscriptionEditModel> {
    return this.http.get<SubscriptionEditModel>(this.baseUri + 'Subscription/PaymentResult?r=' + encodeURIComponent(r) + '&s=' + encodeURIComponent(s));
  }

  /**
    * Előfizetési tranzakciók.
    */
  getTransactions(): Observable<SubscriptionInfo[]> {
    return this.http.get<SubscriptionInfo[]>(this.baseUri + 'Subscription/GetTransactions');
  }

  /**
    * Előfizetési tranzakciók (admin).
    */
  getTransactionsAdmin(userId: string): Observable<SubscriptionInfo[]> {
    return this.http.get<SubscriptionInfo[]>(this.baseUri + 'Subscription/GetTransactionsAdmin?userId=' + encodeURIComponent(userId));
  }

  extendSubscription(userId): Observable<BoolResult> {
    return this.http.get<BoolResult>(this.baseUri + 'Subscription/ExtendPlatinumSubscription?userId=' + encodeURIComponent(userId));
  }

  /**
    * Előfizetési csomagok.
    */
  getPackages(lang: string): Observable<Packages> {
    return this.http.get<Packages>(this.baseUri + 'Subscription/GetProducts?lang=' + encodeURIComponent(lang));
  }

  /**
    * Kuponok lekérdezése (admin).
    */
  getCoupons(): Observable<Coupon[]> {
    return this.http.get<Coupon[]>(this.baseUri + 'Subscription/GetCoupons');
  }

  /**
   * Új kupon létrehozása
   */
  insertCoupon(coupon: any) {
    return this.http.post(
      this.baseUri + 'Subscription/InsertCoupon',
      coupon
    );
  }  
  /**
   * Kupon módosítása
   */
  updateCoupon(couponId: string, data: string) {
  return this.http.put(
    this.baseUri +
      'Subscription/UpdateCoupon?couponId=' +
      encodeURIComponent(couponId),
    JSON.stringify(data),
    {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8'
      }
    }
  );
}
  /**
   * Kupon törlése
   */
  deleteCoupon(couponId: string) {
  return this.http.delete(
    this.baseUri +
      'Subscription/DeleteCoupon?couponId=' +
      encodeURIComponent(couponId)
  );
}
  async extendPlatinumSubscription(userId) {
    let model: BoolResult = {} as BoolResult;
    return new Promise((resolve, reject) => {
      this.extendSubscription(userId).pipe(first()).subscribe({
        next: (v) => model = v,
        error: (e) => reject(e.message),
        complete: () => resolve(model)
      })
    });
  }

  async recreateFailedInvoice(subscriptionId) {
    let model: BoolResult = {} as BoolResult;
    return new Promise((resolve, reject) => {
      this.recreateInvoice(subscriptionId).pipe(first()).subscribe({
        next: (v) => model = v,
        error: (e) => reject(e.message),
        complete: () => resolve(model)
      })
    });
  }

  addSubscription(userId): Observable<BoolResult> {
    return this.http.get<BoolResult>(this.baseUri + 'Subscription/AddPlatinumSubscription?userId=' + encodeURIComponent(userId));
  }

  async addPlatinumSubscription(userId) {
    let model: BoolResult = {} as BoolResult;
    return new Promise((resolve, reject) => {
      this.addSubscription(userId).pipe(first()).subscribe({
        next: (v) => model = v,
        error: (e) => reject(e.message),
        complete: () => resolve(model)
      })
    });
  }

}  